import {
  OptimizelyDecisionWithOutUserContext,
  getExperimentVariables,
  getExperimentVariationKey,
  isExperimentEnabled,
} from '@awaze/mfe-common.lib.optimizely'
import React, {
  ReactNode,
  useContext,
  createContext,
  useEffect,
  useState,
} from 'react'
import { getSpecificExperimentDecision } from '../../utils/optimizely/getSpecificOptimizelyExperiment'

export interface OptimizelyState {
  decisions?: Record<string, OptimizelyDecisionWithOutUserContext>
  dataFile?: string
  userId?: string
  brand?: string
  locale?: string
  userAgent?: string
  host?: string
  isGuestApp?: boolean
}

export interface OptimizelyDecisionsProps extends OptimizelyState {
  isExperimentEnabled: (experimentKey: string) => boolean
  isExperimentVariationKeyOneOf: (
    experimentKey: string,
    variationKeys: Array<string>,
  ) => boolean
  getExperimentVariationKey: (experimentKey: string) => string | undefined
  getExperimentVariables: (
    experimentKey: string,
  ) => OptimizelyDecisionWithOutUserContext['variables'] | undefined
}

const OptimizelyDecisionContext = createContext<OptimizelyDecisionsProps>(null)

export const useOptimizelyDecisionsContext = (): OptimizelyDecisionsProps => {
  return useContext(OptimizelyDecisionContext)
}

export const useSpecificExperimentDecision = (
  experimentKey: string,
  shouldRun: boolean = true, // can be used to run the hook conditionally
  additionalAttributes?: Record<string, string | number | boolean>,
): OptimizelyDecisionWithOutUserContext => {
  const context = useOptimizelyDecisionsContext()
  const [decision, setDecision] =
    useState<OptimizelyDecisionWithOutUserContext | null>(null)

  useEffect(() => {
    if (!shouldRun) {
      return
    }
    const getDecision = async () =>
      await getSpecificExperimentDecision(
        experimentKey,
        context,
        additionalAttributes,
      )
    getDecision().then((decision) => {
      setDecision(decision)
    })
  }, [experimentKey, context, shouldRun])

  return decision
}

export const OptimizelyDecisionContextProvider: React.FC<{
  children: ReactNode
  optimizelyData: OptimizelyState
}> = ({ children, optimizelyData }) => {
  const {
    decisions = {},
    dataFile = '',
    userId,
    userAgent,
    locale,
    brand,
    host,
    isGuestApp,
  }: OptimizelyState = optimizelyData
  return (
    <OptimizelyDecisionContext.Provider
      value={{
        decisions,
        dataFile,
        userId,
        brand,
        locale,
        host,
        userAgent,
        isGuestApp,
        isExperimentEnabled: (experimentKey: string) => {
          return isExperimentEnabled(decisions, experimentKey)
        },
        isExperimentVariationKeyOneOf: (
          experimentKey: string,
          variationKeys: Array<string>,
        ) => {
          const variationKey = getExperimentVariationKey(
            decisions,
            experimentKey,
          )
          return variationKeys.includes(variationKey)
        },
        getExperimentVariationKey: (experimentKey: string) => {
          return getExperimentVariationKey(decisions, experimentKey)
        },
        getExperimentVariables: (experimentKey: string) => {
          return getExperimentVariables(decisions, experimentKey)
        },
      }}
    >
      {children}
    </OptimizelyDecisionContext.Provider>
  )
}
